"use client";

import { Languages } from "lucide-react";
import { useRouter } from "next/navigation";

import { applySupplyLang, type SupplyLang } from "./_lib/lang";

/**
 * First-run language card — shown on `/supply` ONLY while this device has
 * never set the `fb_supply_lang` cookie (page.tsx asks `hasLangCookie()`, not
 * `getSupplyLang()`, because the latter always answers "ms" and so can never
 * tell "chose BM" from "never asked").
 *
 * A CARD, NOT A MODAL, and that is the whole design. A blocking language
 * gate on the first open of a new app is the most likely place for a floor
 * user to bounce — and the default is already the language most of them
 * want. So the dashboard is fully usable behind this: ignoring the card
 * costs nothing, and the user menu's Language section (SupplyUserMenu,
 * 2026-08-17 header redesign — the language toggle moved off the header bar
 * and in with theme) remains the permanent way in.
 *
 * PICKING BM STILL WRITES THE COOKIE. That is not a no-op even though "ms"
 * is the default — writing it is what makes the card disappear, which is the
 * user's actual intent when they tap it. Not writing it would leave the card
 * on screen after a deliberate choice, which reads as a broken button.
 *
 * Mechanism: `applySupplyLang` (cookie write + pre-paint dataset stamp on the
 * [data-supply] root — the same helper SupplyUserMenu's Language section now
 * shares), then router.refresh() to re-run the server tree so every
 * server-picked string swaps. Imports only from the PURE ../_lib/lang —
 * `next/headers` must never reach a client bundle.
 */
const OPTIONS: { lang: SupplyLang; label: string }[] = [
  { lang: "ms", label: "Bahasa Melayu" },
  { lang: "en", label: "English" },
];

export function LangFirstRunPicker() {
  const router = useRouter();

  function choose(event: React.MouseEvent<HTMLButtonElement>, next: SupplyLang) {
    const root = event.currentTarget.closest("[data-supply]");
    applySupplyLang(root instanceof HTMLElement ? root : null, next);
    router.refresh();
  }

  return (
    <section
      data-testid="home-lang-picker"
      className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] p-3.5 shadow-[var(--shadow-card)]"
    >
      <div className="flex items-center gap-2">
        <Languages size={16} className="shrink-0 text-[var(--accent-text)]" aria-hidden="true" />
        <p className="text-[14px] font-extrabold text-[var(--text)]">
          Pilih bahasa anda · Choose your language
        </p>
      </div>
      {/* Both languages side by side on purpose: a person who reads only one
          of them must still be able to find their own without reading the
          other. */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        {OPTIONS.map((option) => (
          <button
            key={option.lang}
            type="button"
            onClick={(event) => choose(event, option.lang)}
            data-testid={`home-lang-${option.lang}`}
            className="flex min-h-[48px] items-center justify-center rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--inner)] px-3 text-[15px] font-extrabold text-[var(--text)] transition active:scale-[0.98]"
          >
            {option.label}
          </button>
        ))}
      </div>
    </section>
  );
}
